import { Box } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { BodyText } from '@/components/atoms/BodyText'
import { FONT_WEIGHT, UI_FONT_SIZE } from '@/shared/styles/typography'
import { RADIUS } from '@/shared/styles/radii'

interface Props {
  speaker: string
  // 任意。録音開始からの時刻など (= 発話セグメントは持たないことがある)。
  time?: string
  text: string
  // 未確定 (interim) 由来の暫定行は italic + muted で示す。
  isInterim?: boolean
  // 0..1、古い行を fade させる用 (HighlightList の newest-N flow)。
  opacity?: number
}

// ハイライト 1 件 (= AI 抽出の要点) を Notion 流に描く 1 ブロック。
// design-system 正典 (docs/design-system/03):
//   - 罫線・カード地色を持たない。区切りは余白とタイポ階層だけ。
//   - 本文を主役にする (body-sm 14 / fg.default)。話者は控えめなタグ (tag 12 / fg.muted)、
//     時刻は二次メタ (micro 11 / fg.faint) として右端へ逃がし、本文の出だしを揃える。
//   - hover は overlay.hover の subtle chrome のみ (fast 120ms)。
export const ConversationLine = ({
  speaker,
  time,
  text,
  isInterim = false,
  opacity = 1,
}: Props) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 1.25,
      py: 1,
      px: 1,
      borderRadius: `${RADIUS.md}px`,
      opacity,
      transition: 'opacity 250ms, background-color 120ms ease',
      '&:hover': { backgroundColor: colorTokens.overlay.hover },
    }}
  >
    {/* 話者: 控えめなタグ (本文を主役にするため muted/medium)。 最長「薬剤師/その他」を
        収める固定幅にして、 行をまたいで本文の出だしを縦に揃える。 */}
    <Box
      component="span"
      sx={{
        width: '3.25em',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        fontSize: UI_FONT_SIZE.tag,
        fontWeight: FONT_WEIGHT.medium,
        color: colorTokens.fg.muted,
      }}
    >
      {speaker}
    </Box>

    {/* 本文 (主役): body-sm / fg.default / ゆとりある行間。 */}
    <BodyText
      sx={{
        flex: 1,
        minWidth: 0,
        whiteSpace: 'pre-wrap',
        fontSize: UI_FONT_SIZE.bodySm,
        lineHeight: 1.65,
        color: colorTokens.fg.default,
        ...(isInterim && {
          fontStyle: 'italic',
          color: colorTokens.fg.muted,
        }),
      }}
    >
      {text}
    </BodyText>

    {/* 時刻: 二次メタ。 本文の出だしを邪魔しないよう右端へ逃がす (micro / fg.faint / 等幅数字)。 */}
    {time && (
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          ml: 1,
          fontSize: UI_FONT_SIZE.micro,
          color: colorTokens.fg.faint,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {time}
      </Box>
    )}
  </Box>
)
